// Submit flow for the polling-unit cancellation report. Local useReducer on purpose (the shared
// useStateHook store is the documented render-loop trap); the delivery itself lives in
// lib/cancellation-sync.ts so it outlives this component — quick-hide closes the panel and the
// report keeps sending.
import { useCallback, useEffect, useReducer, useState } from 'react';

import { getUserData } from '../constants/global';
import type { CancellationReason, EvidenceKind } from '../lib/cancellation-api';
import { putMedia } from '../lib/cancellation-media';
import {
	getRecord,
	type ICancellationRecord,
	type IEvidenceItem,
	newEvidenceItem,
	newRecord,
	requeueEvidence,
	subscribeCancellationChanged,
	updateRecord,
	writeRecord,
} from '../lib/cancellation-store';
import {
	defaultSyncDeps,
	type ISyncDeps,
	syncCancellation,
	type WithdrawOutcome,
	withdrawReport,
} from '../lib/cancellation-sync';

export interface IEvidenceDraft {
	kind: EvidenceKind;
	blob: Blob;
	name: string;
	durationSec?: number;
}

export interface ICancellationDraft {
	reason: CancellationReason;
	notes?: string;
	pollingUnitName?: string;
	evidence: IEvidenceDraft[];
	coords?: ICancellationRecord['coords'];
}

export type SubmitPhase = 'idle' | 'saving' | 'sending' | 'settled';

interface IState {
	phase: SubmitPhase;
	/** Upload percent per evidence item — in memory only, the record holds the durable state. */
	progress: Record<string, number>;
}

type Action =
	| { type: 'saving' }
	| { type: 'sending' }
	| { type: 'settled' }
	| { type: 'progress'; id: string; percent: number }
	| { type: 'reset' };

const initialState: IState = { phase: 'idle', progress: {} };

export const reducer = (state: IState, action: Action): IState => {
	switch (action.type) {
		case 'saving':
			return { ...state, phase: 'saving' };
		case 'sending':
			return { ...state, phase: 'sending' };
		case 'settled':
			return { ...state, phase: 'settled' };
		case 'progress':
			return state.progress[action.id] === action.percent
				? state
				: { ...state, progress: { ...state.progress, [action.id]: action.percent } };
		case 'reset':
			return initialState;
		default:
			return state;
	}
};

const extFor = (mime: string) => {
	if (mime.includes('jpeg')) return 'jpg';
	if (mime.includes('png')) return 'png';
	if (mime.includes('webm')) return 'webm';
	if (mime.includes('mp4')) return mime.startsWith('audio') ? 'm4a' : 'mp4';
	if (mime.includes('ogg')) return 'ogg';
	if (mime.includes('quicktime')) return 'mov';
	return 'bin';
};

/** Stores each blob, then describes it. A blob that can't be stored is still sent this session. */
const persistEvidence = async (drafts: IEvidenceDraft[], now: number): Promise<IEvidenceItem[]> => {
	const items: IEvidenceItem[] = [];
	for (const d of drafts) {
		const id = crypto.randomUUID();
		const mime = d.blob.type || 'application/octet-stream';
		await putMedia(id, d.blob);
		items.push(
			newEvidenceItem({
				id,
				kind: d.kind,
				mime,
				bytes: d.blob.size,
				name: d.name || `${d.kind.toLowerCase()}-${id.slice(0, 8)}.${extFor(mime)}`,
				durationSec: d.durationSec,
				capturedAt: new Date(now).toISOString(),
			})
		);
	}
	return items;
};

export const useCancellationSubmit = (
	electionId: number | undefined,
	puCode: string | undefined,
	deps: ISyncDeps = defaultSyncDeps
) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const read = useCallback(() => (electionId && puCode ? getRecord(electionId, puCode) : null), [electionId, puCode]);
	const [record, setRecord] = useState<ICancellationRecord | null>(read);

	useEffect(() => {
		setRecord(read());
		if (!electionId || !puCode) return;
		return subscribeCancellationChanged(electionId, puCode, () => setRecord(read()));
	}, [electionId, puCode, read]);

	const onProgress = useCallback((id: string, percent: number) => dispatch({ type: 'progress', id, percent }), []);

	const run = useCallback(async () => {
		if (!electionId || !puCode) return;
		dispatch({ type: 'sending' });
		await syncCancellation(electionId, puCode, { force: true, deps, onProgress });
		dispatch({ type: 'settled' });
	}, [electionId, puCode, deps, onProgress]);

	/**
	 * Persist first, then send. The referenceId is minted here — at the Send tap — and stored
	 * on the record, so every retry from any path reuses it.
	 */
	const submit = useCallback(
		async (draft: ICancellationDraft) => {
			if (!electionId || !puCode) return;
			dispatch({ type: 'saving' });
			const now = deps.now();
			const evidence = await persistEvidence(draft.evidence, now);
			writeRecord(
				newRecord({
					referenceId: crypto.randomUUID(),
					electionId,
					userId: Number(getUserData()?.user?.UserId) || undefined,
					pollingUnitCode: puCode,
					pollingUnitName: draft.pollingUnitName,
					reason: draft.reason,
					notes: draft.notes?.trim() || undefined,
					cancelledAt: new Date(now).toISOString(),
					createdAt: now,
					coords: draft.coords ?? null,
					evidence,
				})
			);
			await run();
		},
		[electionId, puCode, deps, run]
	);

	/** Evidence added after filing — same record, same referenceId. */
	const addEvidence = useCallback(
		async (drafts: IEvidenceDraft[]) => {
			if (!electionId || !puCode || !drafts.length) return;
			dispatch({ type: 'saving' });
			const items = await persistEvidence(drafts, deps.now());
			updateRecord(electionId, puCode, (r) => ({ ...r, evidence: [...r.evidence, ...items] }));
			await run();
		},
		[electionId, puCode, deps, run]
	);

	/** Re-attach a file whose bytes were lost: replaces the missing item in place. */
	const reattach = useCallback(
		async (evidenceId: string, draft: IEvidenceDraft) => {
			if (!electionId || !puCode) return;
			const [item] = await persistEvidence([draft], deps.now());
			updateRecord(electionId, puCode, (r) => ({ ...r, evidence: r.evidence.map((e) => (e.id === evidenceId ? item : e)) }));
			await run();
		},
		[electionId, puCode, deps, run]
	);

	/** "Try again" on one refused or parked file: back to the queue, then send now. */
	const retryEvidence = useCallback(
		async (evidenceId: string) => {
			if (!electionId || !puCode) return;
			requeueEvidence(electionId, puCode, evidenceId);
			await run();
		},
		[electionId, puCode, run]
	);

	const withdraw = useCallback(async (): Promise<WithdrawOutcome> => {
		if (!electionId || !puCode) return { ok: false, message: 'Nothing to withdraw.' };
		return withdrawReport(electionId, puCode, deps);
	}, [electionId, puCode, deps]);

	return { ...state, record, submit, retry: run, retryEvidence, addEvidence, reattach, withdraw };
};
